/**
 * @fileoverview My Page's Assigned Banks card.
 * @description The card under the summary: its title and financial year, an optional action beside
 * them, the inline filters, the target table (a status-coloured bank column, target, prospect
 * owner, current status, due date, priority and an actions menu), the empty state and the footer.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useTranslation } from 'react-i18next'

import MyPageFiltersBar from './MyPageFiltersBar'
import MyPageTableRow, { MY_PAGE_GRID_COLUMNS } from './MyPageTableRow'
import TableEmptyState from './TableEmptyState'
import TableFooter from './TableFooter'

import type { MyPageFilterState } from '@/features/sales/lib/my-page-filters.utils'
import type { MyPageTargetAction, MyPageTargetRow } from '@/features/sales/types/my-page.types'
import type { ReactNode } from 'react'

const COLUMN_KEYS = ['bankName', 'targetName', 'assignee', 'stage', 'dueDate', 'priority'] as const

/**
 * @description The message under an empty table: too many results to list, no match for the
 * filters, or nothing assigned.
 * @param {(key: string) => string} t - The translate function.
 * @param {boolean} filtersActive - Whether any filter differs from its default.
 * @param {boolean} tooManyResults - Whether the list was refused for being too long.
 * @returns {string} The message to show.
 */
function emptyMessage(t: (key: string) => string, filtersActive: boolean, tooManyResults: boolean) {
  if (tooManyResults) return t('sales.myWork.tooManyResults')
  return filtersActive ? t('sales.myWork.noResults') : t('sales.myWork.noAssignments')
}

interface MyPageTableCardProps {
  financialYear: string
  headerAction?: ReactNode
  filters: MyPageFilterState
  filtersActive: boolean
  tooManyResults?: boolean
  rows: MyPageTargetRow[]
  onFilterChange: (patch: Partial<MyPageFilterState>) => void
  onClear: () => void
  onOpen: (targetId: string) => void
  onAction: (action: MyPageTargetAction, targetId: string) => void
}

/**
 * @component
 * @param {MyPageTableCardProps} props - The table's data and handlers.
 * @param {string} props.financialYear - The financial year the summary covers, e.g. "2026-27".
 * @param {ReactNode} [props.headerAction] - A control shown at the right of the card's title row.
 * @param {MyPageFilterState} props.filters - The current filters.
 * @param {boolean} props.filtersActive - Whether any filter differs from its default.
 * @param {boolean} [props.tooManyResults] - Whether the list was refused for being too long, so the
 * empty state asks for narrower filters instead of saying nothing matched.
 * @param {MyPageTargetRow[]} props.rows - The targets to list.
 * @param {(patch: Partial<MyPageFilterState>) => void} props.onFilterChange - Called with the
 * filter that changed.
 * @param {() => void} props.onClear - Called when Clear filters is clicked.
 * @param {(targetId: string) => void} props.onOpen - Called to open a target.
 * @param {(action: MyPageTargetAction, targetId: string) => void} props.onAction - Called when a
 * row-menu action is chosen.
 */
function MyPageTableCard({
  financialYear,
  headerAction,
  filters,
  filtersActive,
  tooManyResults = false,
  rows,
  onFilterChange,
  onClear,
  onOpen,
  onAction,
}: MyPageTableCardProps) {
  const { t } = useTranslation()

  return (
    <section className="rounded-[10px] border border-divider bg-white">
      <div className="flex flex-wrap items-center gap-3 px-3.5 pt-4 pb-3.5 sm:px-5">
        <h2 className="text-[15px] font-bold text-ink" data-testid="my-work-section-title">
          {t('sales.myWork.tableTitle')}
        </h2>
        <span className="h-[13px] w-px bg-input-border" aria-hidden="true" />
        <span className="text-[12.5px] text-slate" data-testid="my-work-financial-year">
          {t('sales.myWork.financialYear', { year: financialYear })}
        </span>
        <span className="flex-1" />
        {headerAction}
      </div>

      <MyPageFiltersBar filters={filters} onChange={onFilterChange} onClear={onClear} />

      <div className="overflow-x-auto border-t border-divider">
        <div className="min-w-[960px]">
          <div
            className={`grid ${MY_PAGE_GRID_COLUMNS} items-center gap-3 border-b border-divider bg-input-bg px-4 py-2.5 sm:px-5`}
          >
            {COLUMN_KEYS.map((key) => (
              <span
                key={key}
                className="text-[11px] font-semibold tracking-[0.6px] text-table-header"
              >
                {t(`sales.myWork.columns.${key}`)}
              </span>
            ))}
            <span className="justify-self-end text-[11px] font-semibold tracking-[0.6px] text-table-header">
              {t('sales.myWork.columns.actions')}
            </span>
          </div>

          {rows.map((row) => (
            <MyPageTableRow key={row.targetId} row={row} onOpen={onOpen} onAction={onAction} />
          ))}

          {rows.length === 0 && (
            <TableEmptyState
              message={emptyMessage(t, filtersActive, tooManyResults)}
              {...(filtersActive ? { clearLabel: t('sales.myWork.filters.clear'), onClear } : {})}
            />
          )}
        </div>
      </div>

      {!tooManyResults && (
        <TableFooter line={t('sales.myWork.footerLine', { shown: rows.length })} />
      )}
    </section>
  )
}

export default MyPageTableCard
